import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, BookOpen, LayoutDashboard, Eye, Share2, Upload, Settings } from 'lucide-react';
import ButtonLoader from '../ui/loaders/ButtonLoader';
import ArtmosphereLoader from '../ui/loaders/ArtmosphereLoader';
import AuroraAmber from '../ui/backgrounds/AuroraAmber';

const DesignSystemDemo = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const handleButtonClick = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 2000);
  };

  const handleShowLoader = () => {
    setShowLoader(true);
    setTimeout(() => setShowLoader(false), 3000);
  };

  return (
    <div className="min-h-screen relative">
      {/* Aurora Background */}
      <AuroraAmber intensity={0.2} />
      
      {/* Main Content */}
      <div className="relative z-10 p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-amber-900 mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Artmosphere Design System
            </h1>
            <p className="text-xl text-amber-700" style={{ fontFamily: 'Baloo 2, sans-serif' }}>
              Professional, vibrant, and joyful design with amber-yellow theme
            </p>
          </div>

          {/* Typography Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="card mb-8"
          >
            <h2 className="text-3xl font-bold text-amber-900 mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Typography
            </h2>
            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-amber-900" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Heading 1 - Fredoka Bold
              </h1>
              <h2 className="text-3xl font-bold text-amber-800" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Heading 2 - Fredoka Bold
              </h2>
              <h3 className="text-2xl font-bold text-amber-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Heading 3 - Fredoka Bold
              </h3>
              <p className="text-lg text-amber-700" style={{ fontFamily: 'Baloo 2, sans-serif' }}>
                Body text using Baloo 2 - friendly and readable for all ages. This font provides excellent 
                readability while maintaining the playful, kid-friendly aesthetic that Artmosphere embodies.
              </p>
            </div>
          </motion.section>

          {/* Buttons Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="card mb-8"
          >
            <h2 className="text-3xl font-bold text-amber-900 mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Buttons & Interactions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <button className="btn-primary">
                <Sparkles className="w-5 h-5" />
                Primary Button
              </button>
              
              <ButtonLoader isLoading={isLoading} onClick={handleButtonClick}>
                <Upload className="w-5 h-5" />
                Loading Button
              </ButtonLoader>
              
              <button className="btn-primary">
                <BookOpen className="w-5 h-5" />
                Magic Studio
              </button>
              
              <button className="btn-primary">
                <LayoutDashboard className="w-5 h-5" />
                Dashboard
              </button>
              
              <button className="btn-primary">
                <Eye className="w-5 h-5" />
                Ways of Seeing
              </button>
              
              <button className="btn-primary">
                <Share2 className="w-5 h-5" />
                Share
              </button>
            </div>
          </motion.section>

          {/* Cards Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-8"
          >
            <h2 className="text-3xl font-bold text-amber-900 mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Cards & Components
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Quick Action Card */}
              <motion.div
                whileHover={{ scale: 1.02, y: -2 }}
                className="card"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-xl bg-amber-100 p-2 text-amber-700">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      Magic Studio
                    </h3>
                    <p className="text-sm text-amber-700" style={{ fontFamily: 'Baloo 2, sans-serif' }}>
                      Art transformation
                    </p>
                  </div>
                </div>
                <button className="btn-primary w-full">
                  Explore Magic
                </button>
              </motion.div>

              {/* Assignment Card */}
              <motion.div
                whileHover={{ scale: 1.02, y: -2 }}
                className="card"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="rounded-xl bg-amber-100 p-2 text-amber-700">
                    <BookOpen className="h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-amber-900" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      Color & Mood
                    </h3>
                    <p className="text-sm text-amber-700 mb-2" style={{ fontFamily: 'Baloo 2, sans-serif' }}>
                      Explore warm vs cool palettes
                    </p>
                    <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-amber-100 text-amber-700">
                      Max 3/week
                    </span>
                  </div>
                </div>
                <button className="btn-primary w-full">
                  Start Assignment
                </button>
              </motion.div>

              {/* Artwork Card */}
              <motion.div
                whileHover={{ scale: 1.02, y: -2 }}
                className="card"
              >
                <div className="aspect-square bg-amber-50 rounded-xl mb-4 flex items-center justify-center">
                  <div className="text-amber-400">
                    <Upload className="h-12 w-12" />
                  </div>
                </div>
                <h3 className="font-semibold text-amber-900 mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Student Artwork
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  <button className="inline-flex items-center justify-center gap-1 rounded-xl border px-2 py-2 text-xs hover:bg-amber-50 border-amber-200 text-amber-700">
                    <Sparkles className="h-3.5 w-3.5" /> Appreciation
                  </button>
                  <button className="inline-flex items-center justify-center gap-1 rounded-xl border px-2 py-2 text-xs hover:bg-amber-50 border-amber-200 text-amber-700">
                    <Eye className="h-3.5 w-3.5" /> Reimagine
                  </button>
                  <button className="inline-flex items-center justify-center gap-1 rounded-xl border px-2 py-2 text-xs hover:bg-amber-50 border-amber-200 text-amber-700">
                    <Share2 className="h-3.5 w-3.5" /> Publish
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.section>

          {/* Loaders Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="card mb-8"
          >
            <h2 className="text-3xl font-bold text-amber-900 mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Loaders & Animations
            </h2>
            <div className="flex flex-wrap gap-4">
              <ButtonLoader isLoading={isLoading} onClick={handleButtonClick}>
                <Settings className="w-5 h-5" />
                Test Loading Button
              </ButtonLoader>
              
              <button 
                onClick={handleShowLoader}
                className="btn-primary"
              >
                <Sparkles className="w-5 h-5" />
                Show Full Loader
              </button>
            </div>
          </motion.section>

          {/* Color Palette */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="card"
          >
            <h2 className="text-3xl font-bold text-amber-900 mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Color Palette
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { name: 'Amber 50', class: 'bg-amber-50', hex: '#FFF8E1' },
                { name: 'Amber 100', class: 'bg-amber-100', hex: '#FFECB3' },
                { name: 'Amber 300', class: 'bg-amber-300', hex: '#FFD54F' },
                { name: 'Amber 500', class: 'bg-amber-500', hex: '#FFC107' },
                { name: 'Amber 700', class: 'bg-amber-700', hex: '#FFA000' },
              ].map((color, index) => (
                <div key={index} className="text-center">
                  <div className={`w-16 h-16 rounded-xl ${color.class} mx-auto mb-2 shadow-lg`}></div>
                  <p className="text-sm font-medium text-amber-900" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {color.name}
                  </p>
                  <p className="text-xs text-amber-600" style={{ fontFamily: 'Baloo 2, sans-serif' }}>
                    {color.hex}
                  </p>
                </div>
              ))}
            </div>
          </motion.section>
        </motion.div>
      </div>

      {/* Full Screen Loader */}
      <ArtmosphereLoader 
        isVisible={showLoader} 
        message="Mixing colors... please wait!"
      />
    </div>
  );
};

export default DesignSystemDemo;



